import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileText, Eye, EyeOff, Loader2, AlertCircle, Check, PartyPopper, Gift, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationComplete, setRegistrationComplete] = useState(false);
  
  // Referral and discount states
  const [referralCode, setReferralCode] = useState('');
  const [referrerName, setReferrerName] = useState('');
  const [hasDiscount, setHasDiscount] = useState(false);

  // Check for referral code and discount in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    const discountFlag = searchParams.get('discount');
    
    if (refCode) {
      setReferralCode(refCode);
      // Validate referral code
      api.get(`/referral/validate/${refCode}`)
        .then(res => {
          if (res.data.valid) {
            setReferrerName(res.data.referrer_name);
            toast.success(`You were referred by ${res.data.referrer_name}!`);
          }
        })
        .catch(() => {});
    }
    
    if (discountFlag === 'true') {
      setHasDiscount(true);
    }
  }, [searchParams]);

  const passwordRequirements = [
    { met: password.length >= 8, text: t('register.passwordReq.length') },
    { met: /[A-Z]/.test(password), text: t('register.passwordReq.upper') },
    { met: /[0-9]/.test(password), text: t('register.passwordReq.number') },
  ];

  const isPasswordValid = passwordRequirements.every(req => req.met);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Read values directly from DOM to handle mobile autofill
    const form = e.target;
    const formFullName = form.querySelector('input[id="fullName"]')?.value || fullName;
    const formEmail = form.querySelector('input[type="email"]')?.value || email;
    const formPassword = form.querySelector('input[id="password"]')?.value || password;

    if (!isPasswordValid) {
      setError('Please meet all password requirements');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/register', {
        email: formEmail,
        password: formPassword,
        full_name: formFullName,
        referral_code: referralCode || null
      });
      
      setRegistrationComplete(true);
      toast.success('Account created successfully!');
      
      // If they came with discount flag, redirect to login with discount message
      setTimeout(() => {
        if (hasDiscount) {
          navigate('/login?discount=true');
        } else {
          navigate('/login');
        }
      }, 2000);
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Show success screen after registration
  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <PartyPopper className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('register.success.title')}
          </h1>
          <p className="text-slate-600 mb-6">
            {t('register.success.text')}
          </p>
          
          <div className="flex items-center justify-center gap-2 text-emerald-600 mb-6">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>{t('register.success.redirecting')}</span>
          </div>
          
          <Button
            onClick={() => navigate('/login')}
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
          >
            {t('register.success.button')}
          </Button>
          
          <p className="text-slate-500 text-sm mt-4">
            {t('register.success.hint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <img src="/logo.png" alt="Career Step" className="h-10 w-auto" />
        </Link>

        {/* Referral Banner */}
        {referrerName && (
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl p-4 mb-4 flex items-center gap-3">
            <Users className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-semibold">Referred by {referrerName}!</p>
              <p className="text-sm text-emerald-100">You'll both get rewards when you subscribe</p>
            </div>
          </div>
        )}

        {/* Discount Banner */}
        {hasDiscount && (
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl p-4 mb-4 flex items-center gap-3">
            <Gift className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-semibold">10% First-Time Discount!</p>
              <p className="text-sm text-amber-100">Pay only R22.50 instead of R25 - valid 24 hours after signup</p>
            </div>
          </div>
        )}

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('register.title')}
            </h1>
            <p className="text-slate-600">{t('register.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-slate-700 font-medium">{t('register.fullName')}</Label>
              <Input
                id="fullName"
                data-testid="fullname-input"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('register.fullNamePlaceholder')}
                required
                className="h-12 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">{t('register.email')}</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('register.emailPlaceholder')}
                required
                className="h-12 rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">{t('register.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('register.passwordPlaceholder')}
                  required
                  className="h-12 rounded-xl pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Password Requirements */}
              <div className="space-y-1 mt-2">
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    {req.met ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                    )}
                    <span className={req.met ? 'text-emerald-600' : 'text-slate-500'}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              data-testid="register-btn"
              disabled={loading || !isPasswordValid}
              className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl shadow-lg shadow-amber-500/30"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {t('register.submitting')}
                </>
              ) : (
                t('register.submit')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              {t('register.hasAccount')}{' '}
              <Link to="/login" className="text-amber-600 hover:text-amber-700 font-semibold">
                {t('register.login')}
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-400 text-sm mt-6">
          {t('register.terms')}{' '}
          <Link to="/terms" className="underline hover:text-white">{t('register.termsLink')}</Link>
          {' '}&{' '}
          <Link to="/privacy" className="underline hover:text-white">{t('register.privacyLink')}</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
